'use client';

import type { ReactNode } from 'react';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Text } from '../../../../../components/styled/text';
import { ShieldBanIcon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { HttpRequestDisplay } from '../../HITL/components/HttpRequestDisplay';
import type { HITLContentContext } from '../../tool-config-types';

/**
 * Normalizes http-hitl-executor root-level args to the nested options format
 * that HttpRequestDisplay expects.
 */
function normalizeToNestedFormat(
  args: Record<string, unknown>,
): Record<string, unknown> {
  if (args.options) {
    return args;
  }

  return {
    url: args.url,
    credentialId: args.credentialId,
    bodySignature: args.bodySignature,
    redirect: args.redirect,
    options: {
      method: args.method,
      headers: args.headers,
      body: args.body,
      formData: args.formData,
    },
  };
}

export function HitlExecutorContent({
  args,
  isEditing,
  onSave,
  onCancel,
}: {
  args: Record<string, unknown>;
  isEditing?: boolean;
  onSave?: (args: Record<string, unknown>) => void;
  onCancel?: () => void;
}): ReactNode {
  const t = useTranslations('designSystem.chat');
  const guardrailFeedback =
    typeof args.guardrailFeedback === 'string' ? args.guardrailFeedback : null;
  const normalizedArgs = normalizeToNestedFormat(args);
  const handleSave = onSave
    ? (editedNestedArgs: Record<string, unknown>) => {
        const opts = (editedNestedArgs.options || {}) as Record<
          string,
          unknown
        >;

        onSave({
          ...args,
          url: editedNestedArgs.url,
          credentialId: editedNestedArgs.credentialId,
          bodySignature: editedNestedArgs.bodySignature,
          method: opts.method,
          headers: opts.headers,
          body: opts.body,
          formData: opts.formData,
        });
      }
    : undefined;

  return (
    <VStack gap={3} width="100%">
      {guardrailFeedback && (
        <Box
          px={3}
          py={2}
          borderRadius="md"
          borderWidth="1px"
          borderColor="border.error"
          bg="bg.canvas"
          width="100%"
        >
          <HStack gap={2} mb={1} alignItems="center">
            <Box color="fg.error">
              <ShieldBanIcon size={14} />
            </Box>
            <Text fontSize="xs" fontWeight="medium" color="fg.error">
              {t('guardrailConfirmationPromptCard.guardrailViolationDetails')}
            </Text>
          </HStack>
          <Text fontSize="xs" color="fg.muted" whiteSpace="pre-wrap">
            {guardrailFeedback}
          </Text>
        </Box>
      )}

      <HttpRequestDisplay
        args={normalizedArgs}
        compact={false}
        isEditing={isEditing}
        onSave={handleSave}
        onCancel={onCancel}
      />
    </VStack>
  );
}

export const renderHitlExecutorContent = (
  ctx: HITLContentContext,
): ReactNode => {
  if (!ctx.args) return null;

  return (
    <HitlExecutorContent
      args={ctx.args}
      isEditing={ctx.isEditing}
      onSave={ctx.onSave}
      onCancel={ctx.onCancel}
    />
  );
};
